import {
  GraphQLFulfilmentStatus,
  GraphQLPickListType,
  GraphQLSale,
} from '../factories/gql-types'
import { CustomFieldMap, CustomFieldValue } from '../models/custom-fields'
import { FulfilmentType } from '../models/fulfilment'
import { LineItem } from '../models/line-item'
import { GqlCustomField } from '../queries/sale_create'
import { ReduxCurrentSale } from '../state/slices/currentSale/currentSaleSlice'

export const formatCustomFieldForMutation = (
  customFieldValue: CustomFieldValue
): GqlCustomField => {
  switch (customFieldValue.type) {
    case 'string':
    case 'date':
    case 'product_id':
    case 'qrcode':
      return {
        name: customFieldValue.name,
        stringValue: customFieldValue.value as string,
      }
    case 'integer':
      return {
        name: customFieldValue.name,
        integerValue: customFieldValue.value?.toString(),
      }
    case 'boolean':
      return {
        name: customFieldValue.name,
        booleanValue: customFieldValue.value! as boolean,
      }
    case 'string_array':
      return {
        name: customFieldValue.name,
        stringArrayValue: customFieldValue.value! as string[],
      }
    default:
      return {
        name: customFieldValue.name,
        stringValue: customFieldValue.value as string,
      }
  }
}

export const entityCustomFieldsToMap = (
  sale: Readonly<ReduxCurrentSale>,
  lineItems: Readonly<LineItem[]>
): CustomFieldMap => {
  const lineItemsCustomFields = lineItems.reduce<CustomFieldMap>(
    (acc, lineItem) => {
      const customField = lineItem.customFields.map(customField => {
        return formatCustomFieldForMutation(customField)
      })
      return {
        ...acc,
        [lineItem.localId]: { values: customField, entity: 'LINE_ITEM' },
      }
    },
    {}
  )

  const saleCustomFields = sale.customFields.map(customField => {
    return formatCustomFieldForMutation(customField)
  })

  return {
    ...lineItemsCustomFields,
    [sale.localId]: { values: saleCustomFields, entity: 'SALE' },
  }
}

export const getItemFulfilmentMap = ({
  picklists,
  fulfillments,
}: Pick<GraphQLSale, 'fulfillments' | 'picklists'>) => {
  const itemFulfilmentMap = (picklists || []).reduce(
    (result, { type, lineItems, note }) => {
      for (const { saleLineItem } of lineItems) {
        if (!saleLineItem) {
          continue
        }

        switch (type) {
          case GraphQLPickListType.DISPATCH:
            result.set(saleLineItem.id, [FulfilmentType.DELIVERY, false, note])
            break
          case GraphQLPickListType.PICKUP:
            result.set(saleLineItem.id, [FulfilmentType.PICKUP, false, note])
            break
        }
      }
      return result
    },
    new Map<string, [FulfilmentType, boolean, string]>()
  )

  return (fulfillments || []).reduce((result, { status, lineItems, note }) => {
    for (const { saleLineItem } of lineItems) {
      if (!saleLineItem) {
        continue
      }

      switch (status) {
        case GraphQLFulfilmentStatus.DISPATCHED:
          result.set(saleLineItem.id, [FulfilmentType.DELIVERY, true, note])
          break
        case GraphQLFulfilmentStatus.PICKED_UP:
          result.set(saleLineItem.id, [FulfilmentType.PICKUP, true, note])
          break
      }
    }
    return result
  }, itemFulfilmentMap)
}
